<script lang="ts">
import type { RootProps, RootSlots } from '@vuepic/vue-datepicker';
import { computed, ref, useTemplateRef } from 'vue';

export interface DatepickerSlots extends RootSlots {
}
</script>

<script setup lang="ts" generic="R extends boolean, M extends boolean">
import { VueDatePicker } from '@vuepic/vue-datepicker'
import { fr } from 'date-fns/locale'
import icons from '../styles/icons'
import AwButton from './AwButton.vue'
import '@vuepic/vue-datepicker/dist/main.css'

const props = withDefaults(defineProps<Omit<RootProps, 'modelValue'>>(), {
  locale: () => fr,
  // v11 boolean props were cast to false when absent; v12+ moved enableTimePicker into timeConfig (default true)
  timeConfig: () => ({ enableTimePicker: false }),
})

const slots = defineSlots<DatepickerSlots>()

const modelValue = defineModel<string | Date | Array<string> | Record<"day" | "month" | "year", number> | number>({
  default: undefined,
});
const hostElement = ref<HTMLElement | null>(null);
const vueDatePicker = useTemplateRef('vueDatePicker');

defineExpose({
  closeMenu: () => vueDatePicker.value?.closeMenu(),
});

function resolveParentTeleportRoot(node: HTMLElement | null): HTMLElement | null {
  if (!node) {
    return null;
  }
  // 1) Explicit contract if present
  const explicitRoot = node.closest("[data-aw-teleport-root]");
  if (explicitRoot instanceof HTMLElement) {
    return explicitRoot;
  }

  // // 2) Current AwModal structure fallback
  const modalMainContent = node.closest("#main-content");
  if (modalMainContent instanceof HTMLElement) {
    return modalMainContent;
  }
  return null;
}

const startDate = computed(() => {
  const isString = typeof modelValue.value === "string";
  const isNumber = typeof modelValue.value === "number";
  const isDate = modelValue.value instanceof Date;

  if (isString || isNumber || isDate) {
    return new Date(modelValue.value as string | number | Date);
  }
  return undefined;
});

const resolvedTeleport = computed(() => {
  if (props.teleport) {
    return props.teleport;
  }
  // Auto-detect nearest safe interaction layer
  const parentTeleportRoot = resolveParentTeleportRoot(hostElement.value);
  if (parentTeleportRoot) {
    return parentTeleportRoot;
  }

  // Fallback for overflow clipping outside modals
  return true;
});
</script>

<template>
  <div ref="hostElement">
    <VueDatePicker
      ref="vueDatePicker"
      v-model="modelValue"
      v-bind="props"
      :teleport="resolvedTeleport"
      :config="{ modeHeight: 255, monthChangeOnScroll: false }"
      transitions
      :start-date="startDate"
    >
      <template #arrow-left>
        <AwButton variant="outline" color="secondary" size="sm" :leading-icon="icons.chevronLeft" />
      </template>
      <template #arrow-right>
        <AwButton variant="outline" color="secondary" size="sm" :leading-icon="icons.chevronRight" />
      </template>
      <template #month="{ text }">
        <AwButton class="w-full justify-center" variant="outline" color="secondary" size="sm" :label="text" />
      </template>
      <template v-if="!monthPicker" #year="{ text }">
        <AwButton class="w-full justify-center" variant="outline" color="secondary" size="sm" :label="text" />
      </template>
      <template v-if="!slots.trigger" #trigger>
        <AwButton variant="outline" class="text-center w-full block!">
          <span v-if="modelValue">{{ modelValue }}</span>
          <span v-else class="text-gray-500">Choose Date</span>
        </AwButton>
      </template>
      <template v-for="(_, name) in slots" :key="name" #[name]="slotProps">
        <slot :name v-bind="(slotProps as any) || {}" />
      </template>
    </VueDatePicker>
  </div>
</template>

<style>
  :root {
    --dp-font-family: 'Inter', sans-serif;
  }

  .dp--btn-base.dp--year-select {
    @apply flex items-center justify-center px-2 py-1.5 text-sm font-semibold rounded-lg text-secondary-900 ring-1 ring-secondary-400 h-6.75 w-11.75! hover:bg-secondary-50! cursor-pointer;
  }

  .dp--month-year-wrap .dp--month-year-wrap {
    @apply pl-3! pr-3! flex gap-1.5!;
  }

  .dp--calendar-header-item {
    @apply capitalize;
  }

  .dp--cell-disabled {
    @apply text-secondary-500!;
  }

  .dp--arrow-top,
  .dp--button.dp--overlay-action {
    display: none;
  }

  .dp--menu {
    @apply shadow-lg rounded-lg border-secondary-400 overflow-hidden;
  }

  .dp--month-year-select:first-child {
    @apply ml-0! mr-0!;
  }

  .dp--month-year-select:last-child {
    @apply mr-0! ml-0!;
  }

  .dp--year-mode-picker {
    @apply px-0.75;
  }

  .dp--year-select {
    width: 75%;
  }

  .dp--overlay[aria-label="Year picker overlay"] .dp--overlay-row.dp--flex-row,
  .dp--overlay.dp--overlay-relative .dp--overlay-row.dp--flex-row {
    @apply py-0.75;
  }

  .dp--action-extra {
    @apply pt-3 mx-4 mt--2 relative;
  }

  .dp--action-extra::after {
    @apply content-[''] bg-secondary-200 absolute top-0 left-0 w-full h-px;
  }

  .dp--overlay-cell {
    border: 1px solid;
    @apply border-secondary-200 h-7.5 text-secondary-700 text-md font-medium hover:bg-secondary-100 hover:text-secondary-700;
  }

  .dp--overlay-cell-active {
    border: 1px solid;
    @apply bg-primary-500 border-primary-500 h-7.5 text-md font-medium;
  }

  .dp--menu-inner,
  .dp--menu--inner-stretched,
  .dp--overlay-container.dp--container-block {
    @apply p-4;
  }
  .dp--menu--inner-stretched .dp--overlay-container.dp--container-block {
    @apply p-0!;
  }

  .dp--overlay.dp--overlay-absolute[aria-label="Month picker overlay"] .dp--overlay-container.dp--container-flex {
    @apply px-3 pt-3 pb-4.5;
  }

  .dp--overlay-cell.dp--overlay-cell-pad,
  .dp--overlay-cell-active.dp--overlay-cell-pad,
  .dp--overlay-col {
    height: 49px;
  }

  .dp--overlay-absolute {
    display: grid;
    align-content: center;
  }

  .dp--header-wrap {
    @apply mb-2;
  }

  .dp--year-select, .dp--month-year-select {
    @apply cursor-default;
  }

  .dp--year-select:hover, .dp--month-year-select:hover {
    @apply bg-transparent;
  }

  .dp--inner-nav button {
    @apply h-6.75 w-6.75 justify-center;
  }

  .dp--month-year-select:first-child {
    @apply ml-2;
  }

  .dp--month-year-select:last-child {
    @apply mr-2;
  }

  .dp--calendar-header-item {
    @apply text-md font-medium text-secondary-700;
  }
  .dp--overlay-cell-pad {
    padding: 16px 0;
  }

  .dp--calendar-header-separator {
    @apply bg-secondary-100;
  }

  .dp--cell-offset {
    @apply text-secondary-700!;
  }

  .dp--cell-inner {
    @apply text-secondary-900 text-md font-500 rounded-md w-6 h-6;
  }

  .dp--date-hoverable-end:hover, .dp--date-hoverable-start:hover, .dp--date-hoverable:hover {
    @apply bg-primary-50;
  }

  .dp--range-border-end, .dp--range-border-start, .dp--active {
    @apply bg-primary-500 text-white;
  }

  .dp--today {
    @apply border-none relative;
  }

  .dp--today::after {
    @apply w-0.75 h-0.75 content-[''] bg-primary-500 absolute bottom-0.5 rounded-full left-50% -translate-x-1/2;
  }
  .dp--range-between {
    border-radius: 0;
  }

  .dp--range-border-end {
    border-radius: 0 6px 6px 0;
  }

  .dp--range-border-start {
    border-radius: 6px 0 0 6px;
  }

  .dp--calendar-item:has(.dp--range-between),
  .dp--calendar-item:has(.dp--range-border-start),
  .dp--calendar-item:has(.dp--date-hoverable-start),
  .dp--range-between,
  .dp--range-between-week {
    @apply bg-primary-50;
  }
</style>
